// src/pages/Messages.js — Phase 1 messaging: person/team/everyone,
// optionally attached to a real invoice. Polling-based, same pattern
// as the rest of the portal (checked periodically, not real-time yet).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get, post } from '../utils/api';
import { Spinner, Alert } from '../components/Shared';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  const [recipientType, setRecipientType] = useState('user');
  const [recipientId, setRecipientId] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [attachedInvoice, setAttachedInvoice] = useState(null); // { id, invoice_number, party_name }
  const searchTimer = useRef(null);

  const [imagePreview, setImagePreview] = useState(null); // base64 data URI, for preview + sending
  const [isRecording, setIsRecording] = useState(false);
  const [voiceDataUri, setVoiceDataUri] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const data = await get('/api/messages/inbox');
      setMessages(data.messages || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
    get('/api/users').then(d => setUsers(d.users || [])).catch(() => {});
    get('/api/teams').then(d => setTeams(d.teams || d || [])).catch(() => {});
    const interval = setInterval(loadMessages, 20000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result); // already a data:image/... URI
    reader.readAsDataURL(file);
    setVoiceDataUri(null); // one attachment at a time
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recordChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => setVoiceDataUri(reader.result);
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setImagePreview(null); // one attachment at a time
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch (err) {
      setMsg({ type: 'error', text: 'Microphone access denied or unavailable' });
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    clearInterval(recordTimerRef.current);
  }

  function clearAttachment() {
    setImagePreview(null);
    setVoiceDataUri(null);
  }

  function handleInvoiceQueryChange(v) {
    setInvoiceQuery(v);
    setAttachedInvoice(null);
    clearTimeout(searchTimer.current);
    if (v.trim().length < 2) { setInvoiceResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const data = await get(`/api/messages/search-invoices?q=${encodeURIComponent(v.trim())}`);
        setInvoiceResults(data.invoices || []);
      } catch { /* silent — search is a convenience, not critical */ }
    }, 300);
  }

  function pickInvoice(inv) {
    setAttachedInvoice(inv);
    setInvoiceQuery(inv.invoice_number);
    setInvoiceResults([]);
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!content.trim() && !imagePreview && !voiceDataUri) {
      setMsg({ type: 'error', text: 'Add text, an image, or a voice note' });
      return;
    }
    if (recipientType !== 'everyone' && !recipientId) {
      setMsg({ type: 'error', text: 'Pick a recipient' });
      return;
    }
    setSending(true);
    try {
      await post('/api/messages', {
        recipient_type: recipientType,
        recipient_id: recipientType === 'everyone' ? null : recipientId,
        content: content.trim(),
        related_invoice_id: attachedInvoice?.id || null,
        photoData: imagePreview || null,
        voiceData: voiceDataUri || null,
      });
      setContent('');
      setAttachedInvoice(null);
      setInvoiceQuery('');
      clearAttachment();
      setMsg({ type: 'success', text: 'Message sent' });
      setTimeout(() => setMsg(null), 2000);
      loadMessages();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSending(false);
    }
  }

  async function markRead(id) {
    try {
      await post(`/api/messages/${id}/read`, {});
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch { /* non-critical */ }
  }

  function recipientLabel(m) {
    if (m.recipient_type === 'everyone') return 'Everyone';
    if (m.recipient_type === 'team') return `Team: ${m.team_name || '—'}`;
    return m.recipient_user_name || '—';
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Messages</h1>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      <form onSubmit={sendMessage} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex gap-3 mb-3">
          {[
            { value: 'user', label: 'Person' },
            { value: 'team', label: 'Team' },
            { value: 'everyone', label: 'Everyone' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { setRecipientType(opt.value); setRecipientId(''); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium ${
                recipientType === opt.value ? 'bg-brand-700 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {recipientType === 'user' && (
          <select value={recipientId} onChange={e => setRecipientId(e.target.value)}
            className="w-full mb-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900">
            <option value="">Select a person…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        )}
        {recipientType === 'team' && (
          <select value={recipientId} onChange={e => setRecipientId(e.target.value)}
            className="w-full mb-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900">
            <option value="">Select a team…</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}

        <div className="relative mb-3">
          <input
            type="text"
            value={invoiceQuery}
            onChange={e => handleInvoiceQueryChange(e.target.value)}
            placeholder="Attach an invoice number (optional)…"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900"
          />
          {attachedInvoice && (
            <p className="text-xs text-green-600 mt-1">
              📎 Attached: {attachedInvoice.invoice_number} — {attachedInvoice.party_name}
            </p>
          )}
          {invoiceResults.length > 0 && (
            <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
              {invoiceResults.map(inv => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => pickInvoice(inv)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="font-medium">{inv.invoice_number}</span>
                  <span className="text-gray-400 dark:text-gray-500"> — {inv.party_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <label className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
            📷 Photo
            <input type="file" accept="image/*" capture="environment" onChange={handleImageSelect} className="hidden" />
          </label>

          {!isRecording ? (
            <button type="button" onClick={startRecording}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
              🎤 Voice note
            </button>
          ) : (
            <button type="button" onClick={stopRecording}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm font-medium">
              ⏹ Stop ({recordSeconds}s)
            </button>
          )}

          {(imagePreview || voiceDataUri) && (
            <button type="button" onClick={clearAttachment} className="text-xs text-gray-400 hover:text-red-600">
              Remove attachment
            </button>
          )}
        </div>

        {imagePreview && (
          <img src={imagePreview} alt="Attachment preview" className="max-h-32 rounded-lg mb-3 border border-gray-200 dark:border-gray-700" />
        )}
        {voiceDataUri && (
          <audio controls src={voiceDataUri} className="mb-3 w-full" />
        )}

        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write your message…"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm mb-3 bg-white dark:bg-gray-900"
        />

        <button type="submit" disabled={sending}
          className="px-5 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
          {sending ? 'Sending…' : 'Send'}
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : messages.length === 0 ? (
        <div className="text-center text-gray-400 dark:text-gray-500 py-10">No messages yet</div>
      ) : (
        <div className="space-y-3">
          {messages.map(m => (
            <div
              key={m.id}
              onClick={() => !m.is_read && markRead(m.id)}
              className={`bg-white dark:bg-gray-800 rounded-xl border p-4 cursor-pointer ${
                m.is_read ? 'border-gray-200 dark:border-gray-700' : 'border-accent-500 bg-accent-500/5'
              }`}
            >
              <div className="flex justify-between items-start mb-1">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {m.sender_name} → {recipientLabel(m)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(m.created_at).toLocaleString('en-IN')}
                </p>
              </div>
              {m.invoice_number && (
                <p className="text-xs text-brand-500 mb-1">
                  📎 {m.invoice_number} — {m.party_name}
                </p>
              )}
              {m.content && <p className="text-sm text-gray-700 dark:text-gray-300">{m.content}</p>}
              {m.attachment_type === 'image' && (
                <img src={m.attachment_url} alt="Attachment"
                  onClick={() => setLightboxUrl(m.attachment_url)}
                  className="max-h-48 rounded-lg mt-2 border border-gray-200 dark:border-gray-700 cursor-pointer" />
              )}
              {m.attachment_type === 'voice' && (
                <audio controls src={m.attachment_url} className="mt-2 w-full max-w-xs" />
              )}
              {!m.is_read && <p className="text-xs text-accent-500 mt-1 font-medium">● Unread — tap to mark read</p>}
            </div>
          ))}
        </div>
      )}

      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)}
          className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-6">
          <img src={lightboxUrl} alt="Enlarged" className="max-w-full max-h-[75vh] rounded-lg" />
          <div className="flex gap-3 mt-4">
            <a href={lightboxUrl} download onClick={(e) => e.stopPropagation()}
              className="px-5 py-2.5 bg-brand-700 text-white rounded-xl font-semibold text-sm">
              Download
            </a>
            <button onClick={() => setLightboxUrl(null)}
              className="px-5 py-2.5 bg-white/15 text-white rounded-xl font-semibold text-sm">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
